import React, { useState } from "react";
import PropTypes from "prop-types";
import MultiSelectDropdown from "../multidropdown";

/**
 * Cal.com booking page shown once the HubSpot submission succeeds. `name` and
 * `email` are Cal's standard prefill params, so the booker form on the final
 * step arrives already filled in with what was typed here and the visitor only
 * has to pick a slot.
 */
const CAL_BOOKING_URL = "https://middleware.cal.com/laduram/30min";

const MONITORING_USE_CASES = [
    { value: "OpsAI", id: 1 },
    { value: "Application Performance Monitoring", id: 2 },
    { value: "Infrastructure Monitoring", id: 3 },
    { value: "Log Monitoring", id: 4 },
    { value: "Synthetic Monitoring", id: 5 },
    { value: "Real User Monitoring", id: 6 },
    { value: "Status Page", id: 7 },
    { value: "AWS Monitoring", id: 8 },
    { value: "Azure Monitoring", id: 9 },
    { value: "GCP Monitoring", id: 10 },
    { value: "Database Monitoring", id: 11 },
];

const buildCalUrl = (name: string, email: string) => {
    const params = new URLSearchParams({
        user: "laduram",
        orgRedirection: "true",
        layout: "month_view",
        // Cal follows the visitor's OS dark-mode preference by default, which
        // rendered the booker black inside our light page. Pin it to light.
        theme: "light",
        // Embed mode drops Cal's standalone page shell — the empty band that
        // otherwise sits above "Confirm your details" inside the iframe.
        embed: "true",
        // Hides Cal's event-meta header (host name, "30 Min Meeting", duration,
        // Zoom Video, timezone picker). That block lives inside the iframe, so
        // it is cross-origin and unreachable from our stylesheet — Cal's own
        // embed flag is the only way to suppress it.
        hideEventTypeDetails: "true",
        name,
        email,
    });
    return `${CAL_BOOKING_URL}?${params.toString()}`;
};

const BookADemo = (props) => {
    let isValidInputs = true;
    const [fullname, setFullname] = useState("");
    const [fullnameErr, setFullnameErr] = useState(false);
    const [email, setEmail] = useState("");
    const [emailErr, setEmailErr] = useState(false);
    const [company, setCompany] = useState("");
    const [companyErr, setCompanyErr] = useState(false);
    const [phone, setPhone] = useState("");
    const [monitoringUseCase, setMonitoringUseCase] = useState("");
    const [errorMonitoringUseCase, setErrorMonitoringUseCase] = useState(false);
    const [message, setMessage] = useState("");
    const [messageErr, setMessageErr] = useState(false);
    const [querySentText, setQuerySentText] = useState("");
    // Set from the values that were submitted, not from the live inputs — the
    // fields are cleared on success, and Cal still needs them for prefill.
    const [scheduling, setScheduling] = useState<{ name: string; email: string } | null>(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        validateInputs();
        if (isValidInputs) {
            const submittedName = fullname;
            const submittedEmail = email;

            var xhr = new XMLHttpRequest();
            var url =
                "https://api.hsforms.com/submissions/v3/integration/submit/25310090/ab54942b-da03-4ac5-a8df-92f99796d364";
            var data = {
                submittedAt: Date.now(),
                fields: [
                    {
                        name: "firstname",
                        value: submittedName,
                    },
                    {
                        name: "email",
                        value: submittedEmail,
                    },
                    {
                        name: "company",
                        value: company,
                    },
                    {
                        name: "phone",
                        value: phone,
                    },
                    {
                        name: "message",
                        value: message,
                    },
                    {
                        name: "monitoring_use_case",
                        value: monitoringUseCase,
                    },
                    {
                        name: "source_type",
                        value: "Book a Demo Page",
                    },
                ],
            };
            var final_data = JSON.stringify(data);
            xhr.open("POST", url);
            xhr.setRequestHeader("Content-Type", "application/json");
            xhr.onreadystatechange = function () {
                if (xhr.readyState == 4 && xhr.status == 200) {
                    setEmail("");
                    setFullname("");
                    setCompany("");
                    setPhone("");
                    setMessage("");
                    setMonitoringUseCase("");
                    // Swap the form out for the scheduler. This replaces the old
                    // redirect to /thank-you: the lead is already captured in
                    // HubSpot at this point, so booking a slot is now optional
                    // rather than something a redirect can lose.
                    setScheduling({ name: submittedName, email: submittedEmail });
                } else if (xhr.readyState == 4 && xhr.status == 400) {
                    setQuerySentText(xhr.responseText);
                } else if (xhr.readyState == 4 && xhr.status == 403) {
                    setQuerySentText(xhr.responseText);
                }
            };
            xhr.send(final_data);
        }
    };

    const validateInputs = () => {
        const emailRegEx =
            /^[\w-.]+@(?!gmail\.com$|yahoo\.com$|hotmail\.com$|yahoo\.co\.in$|aol\.com$|abc\.com$|xyz\.com$|pqr\.com$|rediffmail\.com$|live\.com$|outlook\.com$|me\.com$|msn\.com$|ymail\.com$)[\w-]+\.[a-zA-Z]{2,}$/;

        let emailId = email;
        let isInvalidEmail =
            emailId.length < 2 || !emailRegEx.test(String(emailId).toLowerCase());
        let isInvalidName = fullname.length < 3;
        let isInvalidCompany = company.length < 1;
        let isInvalidUsageCase = monitoringUseCase.length < 1;
        let isInvalidMessage = message.length < 3;

        if (isInvalidEmail) {
            setEmailErr(true);
        }
        if (isInvalidName) {
            setFullnameErr(true);
        }
        if (isInvalidCompany) {
            setCompanyErr(true);
        }
        if (isInvalidUsageCase) {
            setErrorMonitoringUseCase(true);
        }
        if (isInvalidMessage) {
            setMessageErr(true);
        }
        // Phone is intentionally not validated — it is the one optional field.
        isValidInputs =
            !isInvalidEmail &&
            !isInvalidName &&
            !isInvalidCompany &&
            !isInvalidUsageCase &&
            !isInvalidMessage;
    };

    if (scheduling) {
        return (
            <div className={"contact-form"}>
                <div className={"form book-demo-scheduler"}>
                    <h4>
                        Thanks{scheduling.name ? `, ${scheduling.name.split(" ")[0]}` : ""}! Now
                        pick a <span className={"text-gradient"}>time that suits you</span>
                    </h4>
                    <div className="form-iframe-contact">
                        <iframe
                            src={buildCalUrl(scheduling.name, scheduling.email)}
                            frameBorder="0"
                            allowFullScreen
                            loading="eager"
                            title="Schedule a demo"
                        ></iframe>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={"contact-form"}>
            <div className={"form"}>
                <h4>Book a Demo</h4>
                <form onSubmit={(e) => handleSubmit(e)}>
                    <div className={"d-flex"}>
                        <div className={"input-wrapper"}>
                            <label>Business Email*</label>
                            <br />
                            <input
                                type={"text"}
                                value={email}
                                placeholder={"Enter email"}
                                className={`${
                                    emailErr ? "is-error contact-input" : "contact-input"
                                }`}
                                onChange={(e) => {
                                    const value = e.target && e.target.value;
                                    setEmail(value);
                                }}
                                onKeyUp={(e) => {
                                    if (email.length > 0) {
                                        setEmailErr(false);
                                    }
                                }}
                            />
                            <h6 className={`form-input-validation ${emailErr ? "error" : ""}`}>
                                {email.length < 1
                                    ? "Email is required"
                                    : "Please enter your business email"}
                            </h6>
                        </div>
                        <div className={"input-wrapper"}>
                            <label>Full Name*</label>
                            <br />
                            <input
                                type={"text"}
                                value={fullname}
                                placeholder={"Full name"}
                                className={`${
                                    fullnameErr ? "is-error contact-input" : "contact-input"
                                }`}
                                onChange={(e) => {
                                    const value = e.target && e.target.value;
                                    setFullname(value);
                                }}
                                onKeyUp={(e) => {
                                    if (fullname.length > 0) {
                                        setFullnameErr(false);
                                    }
                                }}
                            />
                            <h6
                                className={`form-input-validation ${fullnameErr ? "error" : ""}`}
                            >
                                Full name is required
                            </h6>
                        </div>
                    </div>
                    <div className={"d-flex"}>
                        <div className={"input-wrapper"}>
                            <label>Company Name*</label>
                            <br />
                            <input
                                type={"text"}
                                value={company}
                                placeholder={"Enter company name here"}
                                className={`${
                                    companyErr ? "is-error contact-input" : "contact-input"
                                }`}
                                onChange={(e) => {
                                    const value = e.target && e.target.value;
                                    setCompany(value);
                                }}
                                onKeyUp={(e) => {
                                    if (company.length > 0) {
                                        setCompanyErr(false);
                                    }
                                }}
                            />
                            <h6
                                className={`form-input-validation ${companyErr ? "error" : ""}`}
                            >
                                Company name is required
                            </h6>
                        </div>
                        <div className={"input-wrapper"}>
                            <label>Phone Number</label>
                            <br />
                            <input
                                type={"tel"}
                                placeholder={"Your phone number"}
                                value={phone}
                                onChange={(e) => {
                                    const value = e.target && e.target.value;
                                    setPhone(value);
                                }}
                            />
                        </div>
                    </div>

                    <div className={"input-wrapper"}>
                        <MultiSelectDropdown
                            items={MONITORING_USE_CASES}
                            onChange={(selectedItems) => {
                                const selectedValuesString = selectedItems
                                    .map((item) => item.value)
                                    .join(", ");
                                setMonitoringUseCase(selectedValuesString);
                                if (selectedValuesString.length > 0) {
                                    setErrorMonitoringUseCase(false);
                                }
                            }}
                            className={errorMonitoringUseCase}
                            label={"Which monitoring use case are you interested in?"}
                        />
                        <h6
                            className={`form-input-validation ${
                                errorMonitoringUseCase ? "error" : ""
                            }`}
                        >
                            Please select at least one use case
                        </h6>
                    </div>
                    <div className={"input-wrapper"}>
                        <label>Have more details regarding the use case*</label>
                        <br />
                        <textarea
                            value={message}
                            placeholder={"Tell us more about your goals with Middleware"}
                            className={`${
                                messageErr ? "is-error contact-input" : "contact-input"
                            }`}
                            onChange={(e) => {
                                const value = e.target && e.target.value;
                                setMessage(value);
                            }}
                            onKeyUp={(e) => {
                                if (message.length > 0) {
                                    setMessageErr(false);
                                }
                            }}
                        />
                        <h6
                            className={`form-input-validation ${messageErr ? "error" : ""}`}
                        >
                            Minimum 3 characters required
                        </h6>
                    </div>

                    <button type={"submit"} className={"primary arrow"}>
                        Request Demo
                    </button>
                    {querySentText && (
                        <h6 className={"form-input-validation error"}>{querySentText}</h6>
                    )}
                </form>
            </div>
        </div>
    );
};
BookADemo.propTypes = {
    title: PropTypes.string,
    location: PropTypes.string,
};
export default BookADemo;
